/**
 * Hours report — pure derivation.
 *
 * "Who worked where, doing what, for how long": the roster's blocks over a
 * date range, clipped to it, rolled up per worker and per line. A block the
 * allocator placed outside the person's own shift (IsReallocated) counts as
 * overtime. Nothing here touches React or the server.
 */
import { format, parseISO } from "date-fns";

import { RosterAssignment } from "../../../../services/roster-assignments-api";
import { ManufacturingOrder } from "../../production-plan/utils/calculations";

export interface HoursLine {
	workerId: string;
	workerName: string;
	workstation: string;
	jobRole: string;
	/** Distinct yyyy-MM-dd days the person worked this line/role in the range. */
	days: string[];
	hours: number;
	overtimeHours: number;
	/** Products / batches worked, as the plan labels them. */
	products: string[];
}

export interface WorkerHours {
	workerId: string;
	workerName: string;
	hours: number;
	overtimeHours: number;
	days: number;
	lines: HoursLine[];
}

export interface LineHours {
	workstation: string;
	hours: number;
	overtimeHours: number;
	/** Distinct people who worked the line in the range. */
	people: number;
	workers: HoursLine[];
}

export interface HoursReport {
	from: Date;
	/** Exclusive. */
	to: Date;
	workers: WorkerHours[];
	lines: LineHours[];
	totalHours: number;
	totalOvertimeHours: number;
	totalPeople: number;
	/** Assignments whose order was not in the range's plan (named by line only). */
	unmatchedOrders: number;
}

const round2 = (n: number): number => Math.round(n * 100) / 100;

/** Hours of the block that fall inside [from, to). */
export const clippedHours = (a: Pick<RosterAssignment, "startTime" | "endTime">, from: Date, to: Date): number => {
	const start = Math.max(parseISO(a.startTime).getTime(), from.getTime());
	const end = Math.min(parseISO(a.endTime).getTime(), to.getTime());
	return end > start ? (end - start) / 3600000 : 0;
};

export function buildHoursReport(
	assignments: RosterAssignment[],
	orders: ManufacturingOrder[],
	from: Date,
	to: Date
): HoursReport {
	const orderById = new Map(orders.map((o) => [o.id, o]));
	const byKey = new Map<string, HoursLine & { daySet: Set<string>; productSet: Set<string> }>();
	let unmatched = 0;
	assignments.forEach((a) => {
		const hours = clippedHours(a, from, to);
		if (hours <= 0) return;
		const order = orderById.get(a.workOrderId);
		if (!order) unmatched += 1;
		const workstation = (a.workstation || order?.workstation || "—").trim();
		const jobRole = (a.jobRoleName || "—").trim();
		const key = `${a.workerId}::${workstation}::${jobRole}`;
		let row = byKey.get(key);
		if (!row) {
			row = { workerId: String(a.workerId), workerName: a.workerName, workstation, jobRole, days: [], hours: 0, overtimeHours: 0, products: [], daySet: new Set(), productSet: new Set() };
			byKey.set(key, row);
		}
		row.hours += hours;
		if (a.isReallocated) row.overtimeHours += hours;
		row.daySet.add(format(parseISO(a.startTime), "yyyy-MM-dd"));
		const product = (order?.sku || order?.productName || "").trim();
		if (product) row.productSet.add(product);
	});
	const lines: HoursLine[] = Array.from(byKey.values()).map((r) => ({
		workerId: r.workerId,
		workerName: r.workerName,
		workstation: r.workstation,
		jobRole: r.jobRole,
		days: Array.from(r.daySet).sort(),
		hours: round2(r.hours),
		overtimeHours: round2(r.overtimeHours),
		products: Array.from(r.productSet).sort(),
	}));

	const workersMap = new Map<string, WorkerHours & { daySet: Set<string> }>();
	lines.forEach((l) => {
		let w = workersMap.get(l.workerId);
		if (!w) {
			w = { workerId: l.workerId, workerName: l.workerName, hours: 0, overtimeHours: 0, days: 0, lines: [], daySet: new Set() };
			workersMap.set(l.workerId, w);
		}
		w.hours = round2(w.hours + l.hours);
		w.overtimeHours = round2(w.overtimeHours + l.overtimeHours);
		l.days.forEach((d) => w!.daySet.add(d));
		w.lines.push(l);
	});
	const workers: WorkerHours[] = Array.from(workersMap.values())
		.map((w) => ({ ...w, days: w.daySet.size, lines: [...w.lines].sort((a, b) => b.hours - a.hours || a.workstation.localeCompare(b.workstation)) }))
		.sort((a, b) => a.workerName.localeCompare(b.workerName));

	const linesMap = new Map<string, LineHours & { peopleSet: Set<string> }>();
	lines.forEach((l) => {
		let row = linesMap.get(l.workstation);
		if (!row) {
			row = { workstation: l.workstation, hours: 0, overtimeHours: 0, people: 0, workers: [], peopleSet: new Set() };
			linesMap.set(l.workstation, row);
		}
		row.hours = round2(row.hours + l.hours);
		row.overtimeHours = round2(row.overtimeHours + l.overtimeHours);
		row.peopleSet.add(l.workerId);
		row.workers.push(l);
	});
	const lineRows: LineHours[] = Array.from(linesMap.values())
		.map((r) => ({ ...r, people: r.peopleSet.size, workers: [...r.workers].sort((a, b) => a.workerName.localeCompare(b.workerName)) }))
		.sort((a, b) => a.workstation.localeCompare(b.workstation));

	return {
		from,
		to,
		workers,
		lines: lineRows,
		totalHours: round2(lines.reduce((s, l) => s + l.hours, 0)),
		totalOvertimeHours: round2(lines.reduce((s, l) => s + l.overtimeHours, 0)),
		totalPeople: workers.length,
		unmatchedOrders: unmatched,
	};
}

/** "7h 30m" */
export const fmtHours = (hours: number): string => {
	const total = Math.round(hours * 60);
	const h = Math.floor(total / 60);
	const m = total % 60;
	return m ? `${h}h ${String(m).padStart(2, "0")}m` : `${h}h`;
};

const csvCell = (v: string | number): string => {
	const s = String(v);
	return /[",\n]/.test(s) ? `"${s.replace(/"/g, "\"\"")}"` : s;
};

/** One row per worker × line × job, with the days and products, ready to open in Excel. */
export function reportCsv(report: HoursReport): string {
	const header = ["Worker", "Work center", "Job", "Days", "Hours", "Overtime hours", "Products / batches", "From", "To"];
	const rows = report.workers.flatMap((w) =>
		w.lines.map((l) => [
			w.workerName,
			l.workstation,
			l.jobRole,
			l.days.length,
			l.hours,
			l.overtimeHours,
			l.products.join("; "),
			format(report.from, "yyyy-MM-dd"),
			format(new Date(report.to.getTime() - 1), "yyyy-MM-dd"),
		])
	);
	return [header, ...rows].map((r) => r.map(csvCell).join(",")).join("\n");
}
